import { Box, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CustomTextField from '../../../../../common/components/customized/customtextfield/CustomTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import CustomButton from '../../../../../common/components/buttons/CustomButton';
import {
  fileUpload,
  updateTruck
} from '../../../redux/reducer/truckManagementSlices/truckSlice';
import { useAppDispatch, useAppSelector } from '../../../../../app/redux/hooks';
import CustomSelect from '../../../../../common/components/customized/customselect/CustomSelect';
import CustomIconButton from '../../../../../common/components/buttons/CustomIconButton';
import CustomDateCalendar from '../../../../../common/components/customized/customcalendar/CustomCalendar';
import { convertToEpoch2 } from '../../../../../utils/commonFunctions';
import dayjs from 'dayjs';
import Resizer from 'react-image-file-resizer';
import './AddTruckZone.scss';
import CustomFileUpload from '../../../../../common/components/customized/fileupload/CustomFileUpload';

const truckZoneSchema: any = yup.object().shape({
  truckNumber: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9\s]+$/, 'Truck Number must contains only alphabets or numbers')
    .typeError('Truck Number must be a string')
    .required('Enter the Truck Number'),
  unitNumber: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9\s]+$/, 'Unit Number must contains only alphabets or numbers')
    .typeError('Unit Number must be a string')
    .required('Enter the Unit Number'),
  numberOfAxles: yup
    .number()
    .min(1, 'Axle Number must be at least 1')
    .typeError('Axle Number must be a number')
    .required('Enter the Axle Number'),
  yearOfPurchase: yup
    .number()
    .min(1, 'Year of Purchased must be at least 1')
    .typeError('Year of Purchased must be a number')
    .required('Enter the Year of Purchased'),
  truckUnladenWeight: yup
    .number()
    .min(1, 'Truck Unladen Weight must be at least 1')
    .typeError('Truck Unladen Weight must be a number')
    .required('Enter the Truck Unladen Weight'),
  weightUnits: yup
    .string()
    .typeError('Weight Unit must be a string')
    .required('Select Weight Unit'),
  height: yup
    .number()
    .min(1, 'Height must be at least 1')
    .typeError('Height must be a number')
    .required('Enter the Height'),
  dimensionalUnits: yup
    .string()
    .typeError('Dimensional Unit must be a string')
    .required('Select Dimensional Unit'),
  grossVehicleWeight: yup
    .number()
    .min(1, 'Gross Vehicle Weight must be at least 1')
    .typeError('Gross Vehicle Weight must be a number')
    .required('Enter the Gross Vehicle Weight'),
  serialNumber: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9\s]+$/, 'Serial Number must contains only alphabets or numbers')
    .typeError('Serial Number must be a string')
    .required('Enter the Serial Number'),
  dotInspectionDate: yup
    .string()
    .typeError('DOT Inspection Date must be a string')
    .required('Select DOT Inspection Date'),
  insuranceDocument: yup.string().notRequired(),
  insuranceExpiryDate: yup
    .string()
    .typeError('Insurance Expiry Date must be a string')
    .required('Select Insurance Expiry Date'),
  insuranceNumber: yup
    .string()
    .trim()
    .matches(
      /^[A-Za-z0-9\s]+$/,
      'Insurance Number must contains only alphabets or numbers'
    )
    .typeError('Insurance Number must be a string')
    .required('Enter the Insurance Number'),
  licenseDocument: yup.string().notRequired(),
  licenseExpiryDate: yup
    .string()
    .typeError('License Expiry Date be a string')
    .required('Select License Expiry Date'),
  licenseNumber: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9\s]+$/, 'License Number must contains only alphabets or numbers')
    .typeError('License Number must be a string')
    .required('Enter the License Number'),
  fitnessDocument: yup.string().notRequired(),
  fitnessExpiryDate: yup
    .string()
    .typeError('Fitness Expiry Date be a string')
    .required('Select Fitness Expiry Date'),
  fitnessNumber: yup
    .string()
    .trim()
    .matches(/^[A-Za-z0-9\s]+$/, 'Fitness Number must contains only alphabets or numbers')
    .typeError('Fitness Number must be a string')
    .required('Enter the Fitness Number')
});

interface AddTrucksZone {
  open: boolean;
  setOpen: any;
  roleType: string;
  filteredRow: any;
  setPageNo?: any;
  setPageSize?: any;
}

interface TextField {
  id: string;
  label: string;
  field:
    | 'truckNumber'
    | 'unitNumber'
    | 'numberOfAxles'
    | 'yearOfPurchase'
    | 'truckUnladenWeight'
    | 'weightUnits'
    | 'height'
    | 'dimensionalUnits'
    | 'grossVehicleWeight'
    | 'serialNumber'
    | 'dotInspectionDate'
    | 'insuranceDocument'
    | 'insuranceExpiryDate'
    | 'insuranceNumber'
    | 'licenseDocument'
    | 'licenseExpiryDate'
    | 'licenseNumber'
    | 'fitnessDocument'
    | 'fitnessExpiryDate'
    | 'fitnessNumber';

  placeholder: string;
  inputProps: {
    maxLength?: number;
    style?: React.CSSProperties;
  };
  options?: { id: string; label: string }[];
}

const AddTrucksZone = ({
  open,
  setOpen,
  roleType,
  filteredRow,
  setPageNo,
  setPageSize
}: AddTrucksZone) => {
  const [licenseFiles, setLicenseFiles] = useState<File[]>([]);
  const [insuranceFiles, setInsuranceFiles] = useState<File[]>([]);
  const [fitnessFiles, setFitnessFiles] = useState<File[]>([]);
  const dispatch = useAppDispatch();
  const { isLoading, data } = useAppSelector(state => state.updateTruck);

  const { control, handleSubmit, setValue, reset, clearErrors } = useForm({
    resolver: yupResolver(truckZoneSchema)
  });

  const textField: TextField[] = [
    {
      id: '1',
      label: 'Truck Number',
      field: 'truckNumber',
      placeholder: 'Truck Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '2',
      label: 'Unit Number',
      field: 'unitNumber',
      placeholder: 'Unit Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '3',
      label: 'Number of Axle',
      field: 'numberOfAxles',
      placeholder: 'Number of Axle',
      inputProps: { maxLength: 10 }
    },
    {
      id: '4',
      label: 'Year of Purchase',
      field: 'yearOfPurchase',
      placeholder: 'Year of Purchase',
      inputProps: { maxLength: 10 }
    },
    {
      id: '5',
      label: 'Unladen Weight Units',
      field: 'weightUnits',
      placeholder: 'Weight Units',
      inputProps: { maxLength: 10 },
      options: [
        { id: 'kg', label: 'Kg' },
        { id: 'ton', label: 'Ton' },
        { id: 'pounds', label: 'Pounds' }
      ]
    },
    {
      id: '6',
      label: ' Unladen Weight',
      field: 'truckUnladenWeight',
      placeholder: 'Unladen Weight',
      inputProps: { maxLength: 10 }
    },
    {
      id: '7',
      label: 'Dimensional Units',
      field: 'dimensionalUnits',
      placeholder: 'Dimensional Unit',
      inputProps: { maxLength: 10 },
      options: [
        { id: 'm', label: 'm' },
        { id: 'ft', label: 'ft' }
      ]
    },
    {
      id: '8',
      label: 'Height',
      field: 'height',
      placeholder: 'Height',
      inputProps: { maxLength: 10 }
    },
    {
      id: '9',
      label: 'Gross Vehicle Weight',
      field: 'grossVehicleWeight',
      placeholder: 'Gross Vehicle Weight',
      inputProps: { maxLength: 10 }
    },
    {
      id: '10',
      label: 'Serial Number',
      field: 'serialNumber',
      placeholder: 'Serial Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '11',
      label: 'DOT Inspection Date',
      field: 'dotInspectionDate',
      placeholder: 'DOT Inspection Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '12',
      label: 'Insurance Number',
      field: 'insuranceNumber',
      placeholder: 'Insurance Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '13',
      label: 'Insurance Expiry Date',
      field: 'insuranceExpiryDate',
      placeholder: 'Insurance Expiry Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '14',
      label: 'Insurance Document',
      field: 'insuranceDocument',
      placeholder: 'Insurance Document',
      inputProps: { maxLength: 10 }
    },
    {
      id: '15',
      label: 'License Number',
      field: 'licenseNumber',
      placeholder: 'License Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '16',
      label: 'License Expiry Date',
      field: 'licenseExpiryDate',
      placeholder: 'License Expiry Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '17',
      label: 'License Document',
      field: 'licenseDocument',
      placeholder: 'License Document',
      inputProps: { maxLength: 10 }
    },
    {
      id: '18',
      label: 'Fitness Number',
      field: 'fitnessNumber',
      placeholder: 'Fitness Number',
      inputProps: { maxLength: 40 }
    },
    {
      id: '19',
      label: 'Fitness Expiry Date',
      field: 'fitnessExpiryDate',
      placeholder: 'Fitness Expiry Date',
      inputProps: { maxLength: 10 }
    },
    {
      id: '20',
      label: 'Fitness Document',
      field: 'fitnessDocument',
      placeholder: 'Fitness Document',
      inputProps: { maxLength: 10 }
    }
  ];

  const handleUpload = async (type: 'license' | 'insurance' | 'fitness') => {
    const inputElement = document.createElement('input');
    inputElement.type = 'file';
    inputElement.accept = 'image/*';
    inputElement.multiple = true;
    inputElement.onchange = async (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      if (!files) return;
      const fileArray = Array.from(files);
      for (const file of fileArray) {
        Resizer.imageFileResizer(
          file,
          1920,
          1920,
          'JPEG',
          50,
          0,
          (uri: any) => {
            const blob = new Blob([uri], { type: 'image/jpeg' });
            const resizedFile = new File([blob], file.name, { type: 'image/jpeg' });
            if (type === 'license') {
              setLicenseFiles(prev => [...prev, resizedFile]);
            }
            if (type === 'insurance') {
              setInsuranceFiles(prev => [...prev, resizedFile]);
            }
            if (type === 'fitness') {
              setFitnessFiles(prev => [...prev, resizedFile]);
            }
          },
          'blob'
        );
      }
      clearErrors();
    };
    inputElement.click();
  };

  // Function to delete the file
  const handleDelete = (data: any, type: any) => {
    if (type === 'license') {
      const newFiles = licenseFiles?.filter((value: any) => value.name !== data.name);
      setLicenseFiles(newFiles);
    } else if (type === 'insurance') {
      const newFiles = insuranceFiles?.filter((value: any) => value?.name !== data.name);
      setInsuranceFiles(newFiles);
    } else if (type === 'fitness') {
      const newFiles = fitnessFiles?.filter((value: any) => value?.name !== data.name);
      setFitnessFiles(newFiles);
    }
  };

  const onSubmit = async (data: any) => {
    if (filteredRow) {
      let updatePayload: any = {
        dimensionalUnits: data.dimensionalUnits,
        dotInspectionDate: Number(data.dotInspectionDate),
        fitnessModel: {
          documentNames: fitnessFiles.map(file => file.name),
          expireDate: Number(data.fitnessExpiryDate),
          registerNumber: data.fitnessNumber
        },
        grossVehicleWeight: data.grossVehicleWeight,
        height: data.height,
        insuranceModel: {
          documentNames: insuranceFiles.map(file => file.name),
          expireDate: Number(data.insuranceExpiryDate),
          registerNumber: data.insuranceNumber
        },
        licenseModel: {
          documentNames: licenseFiles?.map(file => file?.name),
          expireDate: Number(data.licenseExpiryDate),
          registerNumber: data.licenseNumber
        },
        numberOfAxles: data.numberOfAxles,
        serialNumber: data.serialNumber,
        truckNumber: data.truckNumber,
        truckUnladenWeight: data.truckUnladenWeight,
        unitNumber: data.unitNumber,
        weightUnits: data.weightUnits,
        yearOfPurchase: data.yearOfPurchase
      };
      let payload: { updatePayload: any; id: any } = {
        updatePayload: updatePayload,
        id: filteredRow.id
      };
      const action1 = await dispatch(updateTruck(payload));
      if (action1.type === updateTruck.fulfilled.type) {
        setOpen(false);
        setPageNo(1);
        setPageSize(5);
      }
    }
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const fileUploadAWS = async (files: any) => {
    await dispatch(
      fileUpload({
        files: files
      })
    );
  };

  useEffect(() => {
    setValue('truckNumber', filteredRow?.truckNumber);
    setValue('unitNumber', filteredRow?.unitNumber);
    setValue('numberOfAxles', filteredRow?.numberOfAxles);
    setValue('yearOfPurchase', filteredRow?.yearOfPurchase);
    setValue('truckUnladenWeight', filteredRow?.truckUnladenWeight);
    setValue('weightUnits', filteredRow?.weightUnits);
    setValue('height', filteredRow?.height);
    setValue('dimensionalUnits', filteredRow?.dimensionalUnits);
    setValue('grossVehicleWeight', filteredRow?.grossVehicleWeight);
    setValue('serialNumber', filteredRow?.serialNumber);
    setValue('dotInspectionDate', filteredRow?.dotInspectionDate);
    setValue('insuranceDocument', filteredRow?.insuranceDocumentNames?.join(',') || '');
    setValue('insuranceExpiryDate', filteredRow?.insuranceExpireDate);
    setValue('insuranceNumber', filteredRow?.insuranceRegisterNumber);
    setValue('licenseDocument', filteredRow?.licenseDocumentNames?.join(',') || '');
    setValue('licenseExpiryDate', filteredRow?.licenseExpireDate);
    setValue('licenseNumber', filteredRow?.licenseRegisterNumber);
    setValue('fitnessDocument', filteredRow?.fitnessDocumentNames?.join(',') || '');
    setValue('fitnessExpiryDate', filteredRow?.fitnessExpireDate);
    setValue('fitnessNumber', filteredRow?.fitnessRegisterNumber);
    if (filteredRow?.insuranceDocumentNames) {
      setInsuranceFiles(
        filteredRow.insuranceDocumentNames.map(
          (fileName: string) => new File([], fileName)
        )
      );
    }
    if (filteredRow?.licenseDocumentNames) {
      setLicenseFiles(
        filteredRow.licenseDocumentNames.map((fileName: string) => new File([], fileName))
      );
    }
    if (filteredRow?.fitnessDocumentNames) {
      setFitnessFiles(
        filteredRow.fitnessDocumentNames.map((fileName: string) => new File([], fileName))
      );
    }
  }, [filteredRow]);

  useEffect(() => {
    if (data.data?.length > 0) {
      const combinedFiles = [...licenseFiles, ...insuranceFiles, ...fitnessFiles];
      const filteredFiles = data?.data?.filter(
        (item: any) => Object.keys(item).length !== 0
      );
      const UploadedFiles = combinedFiles?.map(item => {
        const responseFile = filteredFiles.map((file: any) => {
          if (Object.keys(file).includes(item.name)) {
            return { file: item, url: file[item.name] };
          }
        });
        return responseFile;
      });
    }
  }, [data]);

  return (
    <Dialog
      BackdropProps={{ invisible: true }}
      open={open}
      maxWidth={'md'}
      fullWidth
      className='truck-dialog animate__animated animate__zoomInRight'
    >
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px !important',
            padding: '0px 20px'
          }}
        >
          <Box className='closeIcon icon-position d-flex' onClick={handleClose}>
            <CustomIconButton category='CloseValue' />
          </Box>
          <Typography sx={{ fontWeight: 700 }}>{roleType}</Typography>
          <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              component={'form'}
              onSubmit={handleSubmit(onSubmit)}
              className='animate__animated animate__zoomIn animate__slow'
            >
              <Grid container spacing={2}>
                {textField?.map(item => (
                  <Grid
                    item
                    sm={
                      item.id === '3' ||
                      item.id === '4' ||
                      item.id === '5' ||
                      item.id === '6' ||
                      item.id === '7' ||
                      item.id === '8' ||
                      item.id === '12' ||
                      item.id === '13' ||
                      item.id === '15' ||
                      item.id === '16' ||
                      item.id === '18' ||
                      item.id === '19'
                        ? 3
                        : 6
                    }
                    xs={12}
                    key={item.id}
                    sx={
                      item.options
                        ? { mt: 1 }
                        : item.id === '11' ||
                          item.id === '13' ||
                          item.id === '16' ||
                          item.id === '19'
                        ? { mt: 0.5 }
                        : { mt: 0 }
                    }
                  >
                    {item.options ? (
                      <CustomSelect
                        control={control}
                        name={item.field}
                        placeholder={item.placeholder}
                        id={item.id}
                        label={item.label}
                        options={item.options}
                      />
                    ) : item.id === '11' ||
                      item.id === '13' ||
                      item.id === '16' ||
                      item.id === '19' ? (
                      <CustomDateCalendar
                        id={item.id}
                        name={item.field}
                        className='calendar'
                        control={control}
                        disableFuture={false}
                        disablePast={true}
                        placeholder={item.placeholder}
                        label={item.label}
                        value={
                          filteredRow
                            ? dayjs.unix(
                                item.field === 'licenseExpiryDate' &&
                                  filteredRow?.licenseExpireDate !== null
                                  ? filteredRow?.licenseExpireDate
                                  : item.field === 'insuranceExpiryDate' &&
                                    filteredRow?.insuranceExpireDate !== null
                                  ? filteredRow?.insuranceExpireDate
                                  : item.field === 'fitnessExpiryDate' &&
                                    filteredRow?.fitnessExpireDate !== null
                                  ? filteredRow?.fitnessExpireDate
                                  : item.field === 'dotInspectionDate' &&
                                    filteredRow?.dotInspectionDate !== null
                                  ? filteredRow?.dotInspectionDate
                                  : filteredRow?.dotInspectionDate
                              )
                            : null
                        }
                        onDateChange={(date: any) => {
                          const epoc = convertToEpoch2(date?.$d);
                          setValue(item.field, epoc !== null ? String(epoc) : '');
                          clearErrors(item.field);
                        }}
                      />
                    ) : item.field === 'licenseDocument' ||
                      item.field === 'insuranceDocument' ||
                      item.field === 'fitnessDocument' ? (
                      <Grid item sm={12}>
                        <CustomFileUpload
                          handleUpload={() =>
                            handleUpload(
                              item.field === 'licenseDocument'
                                ? 'license'
                                : item.field === 'insuranceDocument'
                                ? 'insurance'
                                : 'fitness'
                            )
                          }
                          label={
                            item.field === 'licenseDocument'
                              ? 'Upload License Document'
                              : item.field === 'insuranceDocument'
                              ? 'Upload Insurance Document'
                              : 'Upload Fitness Document'
                          }
                          files={
                            item.field === 'licenseDocument'
                              ? licenseFiles
                              : item.field === 'insuranceDocument'
                              ? insuranceFiles
                              : fitnessFiles
                          }
                          handleDelete={(e: any) => {
                            handleDelete(
                              e,
                              item.field === 'licenseDocument'
                                ? 'license'
                                : item.field === 'insuranceDocument'
                                ? 'insurance'
                                : 'fitness'
                            );
                          }}
                        />
                      </Grid>
                    ) : (
                      <CustomTextField
                        key={item.id}
                        control={control}
                        name={item.field}
                        placeholder={item.placeholder}
                        id={item.id}
                        type={null}
                        label={item.label}
                        inputProps={item.inputProps}
                        disabled={item.id === '1' ? true : false}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
              <Box
                sx={{
                  marginTop: '5%',
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'flex-end'
                }}
              >
                <CustomButton
                  className='saveChanges'
                  type='submit'
                  category={roleType}
                  loading={isLoading}
                />
                <CustomButton
                  className='cancel'
                  category='Cancel'
                  onClick={() => {
                    handleClose();
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddTrucksZone;
